/* ============================================================
   PUR LUV - Main JavaScript
   Vanilla JS only - no libraries
   ============================================================ */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     CART STATE
  ---------------------------------------------------------- */
  const Cart = {
    // Live cart state, hydrated from Shopify's AJAX Cart API (/cart.js).
    state: { items: [], item_count: 0, items_subtotal_price: 0 },

    // Money formatting uses the shop's money format string, exposed on the
    // drawer element as data-money-format (e.g. "${{amount}}").
    formatMoney(cents) {
      const drawer = document.querySelector('.slide-cart');
      const format = (drawer && drawer.dataset.moneyFormat) || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const withDelims = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format
        .replace(/\{\{\s*amount\s*\}\}/, withDelims)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    },

    shopUrl() {
      const drawer = document.querySelector('.slide-cart');
      return (drawer && drawer.dataset.shopUrl) || '/collections/all';
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        this.state = await res.json();
      } catch (e) { /* leave last-known state */ }
      this.updateUI();
    },

    async add(variantId, qty, options) {
      const opts = options || {};
      if (!variantId) {
        Toast.show('Sorry, that treat is unavailable right now.', 'error');
        return false;
      }
      // A subscription plan picked on the PDP rides along as selling_plan.
      const payload = { id: Number(variantId), quantity: qty || 1 };
      if (opts.sellingPlan) payload.selling_plan = Number(opts.sellingPlan);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          Toast.show(err.description || err.message || 'Could not add to cart.', 'error');
          return false;
        }
        const item = await res.json();
        await this.refresh();
        Toast.show((opts.name || item.product_title) + ' added to cart!', 'cart');
        if (!opts.silent) this.openDrawer();
        return true;
      } catch (e) {
        Toast.show('Could not add to cart. Please try again.', 'error');
        return false;
      }
    },

    // line is 1-based, matching Shopify's /cart/change.js API. Passing
    // sellingPlan (an id, or null for a one-time purchase) switches the
    // line's subscription plan; leaving it undefined keeps the plan as is.
    async changeLine(line, qty, sellingPlan) {
      const payload = { line: line, quantity: qty };
      if (sellingPlan !== undefined) payload.selling_plan = sellingPlan;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          this.state = await res.json();
          this.updateUI();
          return;
        }
        const err = await res.json().catch(() => ({}));
        Toast.show(err.description || err.message || 'Could not update cart.', 'error');
      } catch (e) {
        Toast.show('Could not update cart. Please try again.', 'error');
      }
    },

    // Subscription plans per product handle, keyed by variant id:
    // { handle: { variantId: [{ id, name, quantity }] } }. Filled from
    // /products/<handle>.js the first time a product shows up in the cart.
    plans: {},

    planOptions(item) {
      const byVariant = this.plans[item.handle];
      return (byVariant && byVariant[item.variant_id]) || [];
    },

    async loadPlans() {
      const handles = this.state.items
        .map((item) => item.handle)
        .filter((handle, i, all) => handle && all.indexOf(handle) === i && !(handle in this.plans));
      if (!handles.length) return;
      handles.forEach((handle) => { this.plans[handle] = null; });

      await Promise.all(handles.map(async (handle) => {
        try {
          const res = await fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          const product = await res.json();
          const plans = {};
          (product.selling_plan_groups || []).forEach((group) => {
            group.selling_plans.forEach((plan) => {
              // Bag-count plans carry the number of bags per delivery as a
              // "Product Quantity" option; the line quantity follows it.
              const qtyOption = (plan.options || []).find((o) => o.name === 'Product Quantity');
              plans[plan.id] = { id: plan.id, name: plan.name, quantity: qtyOption ? parseInt(qtyOption.value, 10) || 0 : 0 };
            });
          });
          const byVariant = {};
          product.variants.forEach((variant) => {
            const options = (variant.selling_plan_allocations || [])
              .map((alloc) => plans[alloc.selling_plan_id])
              .filter(Boolean);
            if (options.length) byVariant[variant.id] = options;
          });
          this.plans[handle] = byVariant;
        } catch (e) { /* no plan switcher for this product */ }
      }));

      const found = handles.some((handle) => this.plans[handle] && Object.keys(this.plans[handle]).length);
      if (found) this.renderCartDrawer();
    },

    getTotal() {
      return this.state.items_subtotal_price;
    },

    getCount() {
      return this.state.item_count;
    },

    updateUI() {
      const count = this.getCount();

      // Update all cart count badges
      document.querySelectorAll('.cart-count').forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
        el.classList.add('is-bumped');
        setTimeout(() => el.classList.remove('is-bumped'), 300);
      });

      // Keep the cart trigger's accessible name in sync with the count.
      document.querySelectorAll('.header__cart-btn').forEach((btn) => {
        btn.setAttribute('aria-label', 'Open cart, ' + count + (count === 1 ? ' item' : ' items'));
      });

      // Announce cart changes politely for screen reader users.
      const live = document.getElementById('purluv-cart-live');
      if (live) {
        live.textContent = 'Cart updated: ' + count + (count === 1 ? ' item, ' : ' items, ') +
          'subtotal ' + this.formatMoney(this.getTotal()).replace(/<[^>]*>/g, '');
      }

      // Update slide cart contents
      this.renderCartDrawer();
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    },

    renderCartDrawer() {
      const body = document.querySelector('.slide-cart__body');
      if (!body) return;

      const footer = document.querySelector('.slide-cart__footer');

      if (!this.state.items.length) {
        body.innerHTML = `
          <div class="cart-empty">
            <div class="cart-empty__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><ellipse cx="6.5" cy="10.5" rx="1.9" ry="2.5"/><ellipse cx="10" cy="7.6" rx="2" ry="2.7"/><ellipse cx="14" cy="7.6" rx="2" ry="2.7"/><ellipse cx="17.5" cy="10.5" rx="1.9" ry="2.5"/><path d="M12 11.6c-3.3 0-5.6 2.3-5.6 4.9 0 2.3 2.5 3.3 5.6 3.3s5.6-1 5.6-3.3c0-2.6-2.3-4.9-5.6-4.9z"/></svg></div>
            <h3 class="cart-empty__title">Your Cart is Empty</h3>
            <p class="cart-empty__message">Time to share the luv! Browse our treats.</p>
            <a href="${this.shopUrl()}" class="btn btn--primary" data-action="close-cart">Shop Treats</a>
          </div>`;
        if (footer) footer.style.display = 'none';
        return;
      }

      if (footer) footer.style.display = 'block';

      const drawer = document.querySelector('.slide-cart');
      const oneTimeLabel = (drawer && drawer.dataset.labelOneTime) || 'One-time purchase';

      let html = '<div class="cart-items">';

      this.state.items.forEach((item, i) => {
        const line = i + 1;
        const name = this.escapeHtml(item.product_title);
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? this.escapeHtml(item.variant_title) : '';
        const image = item.image
          ? item.image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_160x160$1$2') : '';
        // Subscription plan: a select once the product's plans are known
        // (plans for this variant plus a one-time purchase), otherwise the
        // current plan's name as plain text. A subscription line has no
        // quantity stepper: the plan fixes the bag count.
        const alloc = item.selling_plan_allocation;
        const planId = alloc ? alloc.selling_plan.id : null;
        const planOptions = this.planOptions(item);
        let plan = '';
        if (planOptions.length) {
          plan = `<select class="cart-item__plan-select" data-line="${line}" aria-label="Purchase option for ${name}">
              <option value=""${planId ? '' : ' selected'}>${this.escapeHtml(oneTimeLabel)}</option>
              ${planOptions.map((o) => `<option value="${o.id}"${o.id === planId ? ' selected' : ''}>${this.escapeHtml(o.name)}</option>`).join('')}
            </select>`;
        } else if (alloc) {
          plan = `<div class="cart-item__plan">${this.escapeHtml(alloc.selling_plan.name)}</div>`;
        }
        html += `
        <div class="cart-item" data-line="${line}">
          <div class="cart-item__image">
            ${image ? `<a href="${item.url}"><img src="${image}" alt="${name}" width="80" height="80" loading="lazy"></a>` : ''}
          </div>
          <div class="cart-item__details">
            <div class="cart-item__head">
              <div class="cart-item__title"><a href="${item.url}">${name}</a></div>
              <button class="cart-item__remove" data-line="${line}" aria-label="Remove ${name}">
                <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
              </button>
            </div>
            <div class="cart-item__variant">${variant}</div>
            ${plan}
            <div class="cart-item__bottom">
              ${alloc ? '' : `<div class="cart-item__quantity">
                <button class="cart-item__qty-btn" data-action="decrease" data-line="${line}" aria-label="Decrease quantity">&#8722;</button>
                <span class="cart-item__qty-value">${item.quantity}</span>
                <button class="cart-item__qty-btn" data-action="increase" data-line="${line}" aria-label="Increase quantity">&#43;</button>
              </div>`}
              <span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>`;
      });

      html += '</div>';

      body.innerHTML = html;

      // Update subtotal
      const subtotalEl = document.querySelector('.slide-cart__subtotal-price');
      if (subtotalEl) subtotalEl.textContent = this.formatMoney(this.getTotal());

      // Bind events
      body.querySelectorAll('.cart-item__qty-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const line = parseInt(btn.dataset.line, 10);
          const item = Cart.state.items[line - 1];
          if (!item) return;
          const delta = btn.dataset.action === 'increase' ? 1 : -1;
          Cart.changeLine(line, item.quantity + delta);
        });
      });

      body.querySelectorAll('.cart-item__remove').forEach((btn) => {
        btn.addEventListener('click', () => {
          Cart.changeLine(parseInt(btn.dataset.line, 10), 0);
        });
      });

      body.querySelectorAll('.cart-item__plan-select').forEach((select) => {
        select.addEventListener('change', async () => {
          const line = parseInt(select.dataset.line, 10);
          const item = Cart.state.items[line - 1];
          if (!item) return;
          const planId = select.value ? Number(select.value) : null;
          const option = Cart.planOptions(item).find((o) => o.id === planId);
          // A plan sets its own bag count; back to one-time starts at 1.
          const qty = option ? (option.quantity || item.quantity) : 1;
          await Cart.changeLine(line, qty, planId);
          // The drawer re-rendered; hand focus back to this line's select.
          const next = document.querySelector('.cart-item__plan-select[data-line="' + line + '"]');
          if (next) next.focus();
        });
      });

      this.loadPlans();
    },

    lastFocused: null,

    openDrawer() {
      const cart = document.querySelector('.slide-cart');
      const overlay = document.querySelector('.cart-overlay');
      this.lastFocused = document.activeElement;
      if (cart) {
        cart.classList.add('is-open');
        cart.setAttribute('aria-hidden', 'false');
        cart.inert = false;
      }
      if (overlay) overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      // Focus first interactive element
      const closeBtn = cart && cart.querySelector('.slide-cart__close');
      if (closeBtn) closeBtn.focus();
    },

    closeDrawer() {
      const cart = document.querySelector('.slide-cart');
      const overlay = document.querySelector('.cart-overlay');
      if (cart) {
        cart.classList.remove('is-open');
        cart.setAttribute('aria-hidden', 'true');
        cart.inert = true;
      }
      if (overlay) overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      // Return focus to whatever opened the drawer.
      if (this.lastFocused && this.lastFocused.focus && document.contains(this.lastFocused)) {
        this.lastFocused.focus();
      }
      this.lastFocused = null;
    },
  };

  /* ----------------------------------------------------------
     TOAST NOTIFICATIONS
  ---------------------------------------------------------- */
  const Toast = {
    show(message, type = 'success') {
      const container = document.querySelector('.toast-container');
      if (!container) return;

      const toast = document.createElement('div');
      toast.className = 'toast toast--' + type;
      // Errors interrupt (alert); success/cart confirmations wait
      // politely so they don't clobber what's being read.
      toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
      toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
      toast.innerHTML = `<span class="toast__accent"></span><span>${message}</span>`;
      container.appendChild(toast);

      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });

      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.remove(), 300);
      }, 4000);
    },
  };

  /* ----------------------------------------------------------
     DOG-FIRST MICROINTERACTIONS
     Decorative, on-brand flourishes. Everything no-ops when the
     visitor prefers reduced motion.
  ---------------------------------------------------------- */
  const Delight = {
    reduceMotion:
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,

    PAW_SVG:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><ellipse cx="6.5" cy="10.5" rx="1.9" ry="2.5"/><ellipse cx="10" cy="7.6" rx="2" ry="2.7"/><ellipse cx="14" cy="7.6" rx="2" ry="2.7"/><ellipse cx="17.5" cy="10.5" rx="1.9" ry="2.5"/><path d="M12 11.6c-3.3 0-5.6 2.3-5.6 4.9 0 2.3 2.5 3.3 5.6 3.3s5.6-1 5.6-3.3c0-2.6-2.3-4.9-5.6-4.9z"/></svg>',
    TREAT_SVG:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9c4-3 14-3 18 0 1.2 1 1.2 4 0 5-4 3-14 3-18 0-1.2-1-1.2-4 0-5z" fill="#9a5226"/><circle cx="9" cy="11.5" r="1" fill="#5f3015" opacity="0.55"/><circle cx="14" cy="12" r="1" fill="#5f3015" opacity="0.55"/></svg>',
    TAIL_SVG:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 21c-1-6 1-12 6-16 2-1.6 4.6-2 5.2-0.3 0.5 1.4-0.8 2.6-2.2 3.6-3.8 2.8-6 7-6.4 12-0.1 1.6-2.4 1.7-2.6 0.7z"/></svg>',

    layer() {
      let el = document.getElementById('delight-layer');
      if (!el) {
        el = document.createElement('div');
        el.id = 'delight-layer';
        el.className = 'delight-layer';
        el.setAttribute('aria-hidden', 'true');
        document.body.appendChild(el);
      }
      return el;
    },

    spawn(cls, html, x, y, life, rot) {
      const node = document.createElement('span');
      node.className = cls;
      node.style.left = x + 'px';
      node.style.top = y + 'px';
      if (rot != null) node.style.setProperty('--paw-rot', rot + 'deg');
      node.innerHTML = html;
      this.layer().appendChild(node);
      setTimeout(() => node.remove(), life);
    },

    // A trail of paw prints that follows the cursor across the hero.
    // Spacing + time throttle keep it sparse — fast/erratic mouse
    // movement won't pile prints on top of each other.
    pawTrail(scope) {
      if (this.reduceMotion || !scope) return;
      let lastX = null, lastY = null, lastTime = 0, step = 0;
      const self = this;
      const MIN_DIST = 120; // px the cursor must travel between prints
      const MIN_GAP = 120;  // ms minimum between prints (caps fast bursts)
      scope.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'touch') return;
        if (lastX === null) { lastX = e.clientX; lastY = e.clientY; return; }
        const now = e.timeStamp || 0;
        if (now - lastTime < MIN_GAP) return;
        const dx = e.clientX - lastX, dy = e.clientY - lastY;
        if (Math.hypot(dx, dy) < MIN_DIST) return;
        lastX = e.clientX; lastY = e.clientY; lastTime = now;
        step++;
        const theta = Math.atan2(dy, dx);
        const side = step % 2 ? 1 : -1;
        const perp = theta + Math.PI / 2;
        const px = e.clientX + Math.cos(perp) * 8 * side;
        const py = e.clientY + Math.sin(perp) * 8 * side;
        const rot = (theta * 180) / Math.PI + 90 + side * 7;
        self.spawn('paw', self.PAW_SVG, px, py, 950, rot);
      });
      scope.addEventListener('pointerleave', () => { lastX = null; lastY = null; });
    },

    // A jerky treat that drops from an element — quick-add press feedback.
    treatDrop(el) {
      if (this.reduceMotion || !el) return;
      const r = el.getBoundingClientRect();
      this.spawn('delight-treat', this.TREAT_SVG, r.left + r.width / 2, r.top + r.height / 2, 800);
    },

    // A happy tail that wags above an element — cart-added confirmation.
    tailWag(el) {
      if (this.reduceMotion || !el) return;
      const r = el.getBoundingClientRect();
      this.spawn('delight-tail', this.TAIL_SVG, r.left + r.width / 2, r.top + r.height / 2, 820);
    },
  };

  function initDelights() {
    // Paw-trail hover animation removed per client feedback.
    // Opt back in on a specific element with [data-paw-trail] if it returns.
    Delight.pawTrail(document.querySelector('[data-paw-trail]'));
  }

  /* ----------------------------------------------------------
     ANNOUNCEMENT BAR ROTATION
  ---------------------------------------------------------- */
  function initAnnouncementBar() {
    const messages = document.querySelectorAll('.announcement-bar__message');

    // Dismiss (the visible "stop/hide" control WCAG 2.2.2 asks for)
    const closeBtn = document.querySelector('.announcement-bar__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        const bar = document.querySelector('.announcement-bar');
        if (bar) bar.style.display = 'none';
      });
    }

    if (messages.length < 2) return;

    let current = 0;
    const sync = () => {
      messages.forEach((m, i) => {
        m.classList.toggle('is-active', i === current);
        // Only the visible message should reach screen readers.
        m.setAttribute('aria-hidden', i === current ? 'false' : 'true');
      });
    };
    sync();

    // Auto-rotation is motion — skip it entirely for reduced-motion users.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    setInterval(() => {
      current = (current + 1) % messages.length;
      sync();
    }, 5000);
  }

  /* ----------------------------------------------------------
     MOBILE MENU — premium slide-in with categories + accordion
     Replaces the legacy link list with a full shopping surface.
  ---------------------------------------------------------- */
  function initMobileMenu() {
    const toggle = document.querySelector('.header__menu-toggle');
    const menu = document.querySelector('.mobile-menu');
    if (!toggle || !menu) return;

    // Data — single source of truth for mobile nav, rendered by the
    // purluv-header section as a JSON script tag so the theme editor's
    // menus and section blocks drive the drawer content.
    let menuData = {};
    const dataEl = document.getElementById('purluv-menu-data');
    if (dataEl) {
      try { menuData = JSON.parse(dataEl.textContent); } catch (e) { menuData = {}; }
    }

    const HOME_URL = menuData.homeUrl || '/';
    const LOGO_SRC = menuData.logo || '';

    const PRIMARY = (menuData.primary && menuData.primary.length)
      ? menuData.primary
      : Array.from(menu.querySelectorAll('a')).map((a) => ({
          label: a.textContent.trim(),
          href: a.getAttribute('href'),
        }));

    // Featured card from the header section's JSON (the protein list is
    // desktop-only).
    const FEATURE = menuData.feature || null;

    // Inline SVG constants
    const ARROW = '<svg class="mm-arrow" viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 6 15 12 9 18"/></svg>';
    const CLOSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>';
    const BAG   = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>';

    // Build primary list — flat links only, no accordions.
    const primaryHtml = PRIMARY.map((item) => `
      <a class="mm-primary-link" href="${item.href}">
        <span>${item.label}</span>
        ${ARROW}
      </a>
    `).join('');

    const featureHtml = FEATURE ? `
      <a class="mega__feature${FEATURE.style === 'aj' ? ' mega__feature--aj' : ''}" href="${FEATURE.href}">
        <div class="mega__feature-media"><img src="${FEATURE.image}" alt="${FEATURE.alt}" loading="lazy"></div>
        <div class="mega__feature-body">
          <span class="mega__feature-eyebrow">${FEATURE.eyebrow}</span>
          <h3 class="mega__feature-title">${FEATURE.title}</h3>
          <p class="mega__feature-desc">${FEATURE.desc}</p>
          <span class="btn btn--primary btn--small">${FEATURE.button}</span>
        </div>
      </a>
    ` : '';

    // Replace menu innerHTML with the clean structure (no Support section).
    menu.setAttribute('role', 'dialog');
    menu.setAttribute('aria-modal', 'true');
    menu.setAttribute('aria-label', 'Main navigation');
    menu.innerHTML = `
      <div class="mm-backdrop" data-mm-close></div>
      <aside class="mm-panel">
        <header class="mm-topbar">
          <a href="${HOME_URL}" class="mm-logo" aria-label="Pur Luv home">${LOGO_SRC ? `<img src="${LOGO_SRC}" alt="Pur Luv" class="brand-logo">` : ''}</a>
          <button class="mm-close" type="button" aria-label="Close menu" data-mm-close>${CLOSE}</button>
        </header>

        <nav class="mm-primary" aria-label="Primary navigation">
          ${primaryHtml}
        </nav>

        ${featureHtml ? `
        <section class="mm-block mm-block--feature" aria-label="Featured">
          <span class="mega__eyebrow">${menuData.featureTitle || 'Featured'}</span>
          ${featureHtml}
        </section>` : ''}
      </aside>
    `;

    const panel = menu.querySelector('.mm-panel');
    const closeBtns = menu.querySelectorAll('[data-mm-close]');

    // Closed at load — keep the panel out of the tab order and
    // accessibility tree until it is opened.
    menu.inert = true;

    // Open / close
    function openMenu() {
      menu.inert = false;
      menu.classList.add('is-open');
      document.documentElement.classList.add('mm-locked');
      toggle.setAttribute('aria-expanded', 'true');
      // Focus the close button for keyboard users (delay past animation)
      setTimeout(() => {
        const closeBtn = menu.querySelector('.mm-close');
        if (closeBtn) closeBtn.focus();
      }, 100);
    }

    function closeMenu() {
      menu.inert = true;
      menu.classList.remove('is-open');
      document.documentElement.classList.remove('mm-locked');
      toggle.setAttribute('aria-expanded', 'false');
      // Reset accordion state
      menu.querySelectorAll('.mm-sub.is-open').forEach((s) => s.classList.remove('is-open'));
      menu.querySelectorAll('.mm-primary-expand[aria-expanded="true"]')
        .forEach((b) => b.setAttribute('aria-expanded', 'false'));
      toggle.focus();
    }

    toggle.addEventListener('click', openMenu);
    closeBtns.forEach((btn) => btn.addEventListener('click', closeMenu));

    // Accordion triggers
    menu.querySelectorAll('.mm-primary-expand').forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('aria-controls');
        const sub = menu.querySelector('#' + targetId);
        if (!sub) return;
        const isOpen = sub.classList.contains('is-open');
        sub.classList.toggle('is-open', !isOpen);
        btn.setAttribute('aria-expanded', String(!isOpen));
      });
    });

    // Close on any real navigation link tap
    menu.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', (e) => {
        // Ignore logo taps that would reload the current page mid-close
        const href = link.getAttribute('href');
        if (href === '#' || href.startsWith('javascript:')) return;
        // Let the navigation happen; just collapse visually
        closeMenu();
      });
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });

    // Simple focus trap
    menu.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusables = menu.querySelectorAll(
        'a[href], button:not([disabled])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ----------------------------------------------------------
     PL CAROUSEL — reusable premium carousel initializer
     Powers all `[data-pl-carousel]` instances on the site.
     Features: next/prev, click-pagination progress dots,
     keyboard arrows, swipe, optional autoplay (data-autoplay="<ms>"),
     pause-on-hover/focus, and prefers-reduced-motion.
  ---------------------------------------------------------- */
  function initPlCarousel() {
    const roots = document.querySelectorAll('[data-pl-carousel]');
    if (!roots.length) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    roots.forEach((root) => {
      const track = root.querySelector('[data-pl-track]');
      const slides = track ? Array.from(track.querySelectorAll('.pl-carousel__slide')) : [];
      const prevButtons = root.querySelectorAll('[data-pl-prev]');
      const nextButtons = root.querySelectorAll('[data-pl-next]');
      const dotsWraps = root.querySelectorAll('[data-pl-dots]');
      if (!track || slides.length === 0) return;

      let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
      if (index < 0) index = 0;

      const autoplayMs = parseInt(root.getAttribute('data-autoplay'), 10);
      let timer = null;
      let leaveTimer = null;
      const TRANSITION_MS = 540; // matches CSS .pl-carousel__slide transition + small buffer
      // Expose the autoplay duration as a CSS custom property so the active
      // pagination dot's fill animation runs in sync with the slide timer.
      // Carousels without autoplay get an "instant" mode (full fill, no anim).
      const hasAutoplay = autoplayMs && !reducedMotion;
      if (hasAutoplay) {
        root.style.setProperty('--pl-fill-duration', autoplayMs + 'ms');
      }

      // WCAG 2.2.2: auto-rotating content needs an explicit pause control.
      let userPaused = false;
      const pauseButtons = root.querySelectorAll('[data-pl-pause]');

      // Polite live region announcing the current slide to screen readers.
      const liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      root.appendChild(liveRegion);

      function show(i, dirOverride) {
        const total = slides.length;
        const newIndex = ((i % total) + total) % total;
        const oldIndex = index;
        const sameSlide = newIndex === oldIndex && slides[newIndex].classList.contains('is-active');
        if (sameSlide) { renderDots(); return; }

        // Direction: explicit override (button click / autoplay) wins; else infer
        // by comparing indices, with wrap-around respecting the requested direction.
        const direction = dirOverride
          || (newIndex === 0 && oldIndex === total - 1 ? 'next'
            : newIndex === total - 1 && oldIndex === 0 ? 'prev'
            : newIndex > oldIndex ? 'next' : 'prev');
        root.setAttribute('data-direction', direction);
        root.classList.add('is-animating');

        index = newIndex;

        // Clear any in-flight is-leaving from a previous, still-running transition
        if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
        slides.forEach((s) => s.classList.remove('is-leaving'));

        // Set the outgoing slide into is-leaving so its drift-out animation plays
        if (oldIndex !== newIndex) {
          const leaving = slides[oldIndex];
          leaving.classList.remove('is-active');
          leaving.classList.add('is-leaving');
          leaving.removeAttribute('hidden'); // stay visible during the fade
        }

        // Activate the incoming slide
        slides.forEach((slide, idx) => {
          if (idx === index) {
            slide.classList.add('is-active');
            slide.classList.remove('is-leaving');
            slide.removeAttribute('hidden');
          } else if (idx !== oldIndex) {
            slide.classList.remove('is-active', 'is-leaving');
            slide.setAttribute('hidden', '');
          }
        });

        // After the transition window, drop the leaving slide and the GPU layer hint
        leaveTimer = setTimeout(() => {
          slides.forEach((s) => {
            if (s.classList.contains('is-leaving')) {
              s.classList.remove('is-leaving');
              if (!s.classList.contains('is-active')) s.setAttribute('hidden', '');
            }
          });
          root.classList.remove('is-animating');
          leaveTimer = null;
        }, TRANSITION_MS);

        renderDots();
      }

      function renderDots() {
        dotsWraps.forEach((dotsWrap) => {
          if (!dotsWrap.children.length) {
            slides.forEach((_, i) => {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'pl-carousel__progress-dot';
              btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
              btn.addEventListener('click', () => {
                stop();
                show(i, i > index ? 'next' : 'prev');
                start();
              });
              dotsWrap.appendChild(btn);
            });
          }
          Array.from(dotsWrap.children).forEach((dot, i) => {
            const wasActive = dot.classList.contains('is-active');
            const willBeActive = i === index;
            dot.setAttribute('aria-current', willBeActive ? 'true' : 'false');
            // Reset the fill state, then re-arm. For autoplay carousels the
            // dot's CSS transition runs from 0 → 100% over the autoplay
            // window. For non-autoplay we mark .is-instant so the active
            // dot snaps full instead of animating across some random window.
            dot.classList.remove('is-active', 'is-instant');
            if (willBeActive) {
              if (!hasAutoplay) dot.classList.add('is-instant');
              // Force a reflow so the next class add restarts the transition.
              // eslint-disable-next-line no-unused-expressions
              void dot.offsetWidth;
              dot.classList.add('is-active');
            }
          });
        });
      }

      function go(dir) { show(index + dir, dir > 0 ? 'next' : 'prev'); }

      function announce() {
        const slide = slides[index];
        const label = slide.getAttribute('aria-label') || 'Slide ' + (index + 1) + ' of ' + slides.length;
        liveRegion.textContent = label;
      }

      function start() {
        if (!autoplayMs || reducedMotion || userPaused) return;
        stop();
        timer = setInterval(() => show(index + 1), autoplayMs);
        // Resume the dot fill animation in lockstep with the timer.
        root.classList.remove('is-paused');
      }
      function stop() {
        if (timer) { clearInterval(timer); timer = null; }
        // Pause the dot fill animation so the orange progress indicator
        // freezes at the same moment the autoplay timer pauses.
        if (hasAutoplay) root.classList.add('is-paused');
      }

      prevButtons.forEach((b) => b.addEventListener('click', () => { stop(); go(-1); announce(); start(); }));
      nextButtons.forEach((b) => b.addEventListener('click', () => { stop(); go(1); announce(); start(); }));

      pauseButtons.forEach((b) => {
        // aria-pressed=true means "paused" (the play icon is showing).
        b.setAttribute('aria-pressed', 'false');
        b.addEventListener('click', () => {
          userPaused = !userPaused;
          b.setAttribute('aria-pressed', String(userPaused));
          b.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
          if (userPaused) stop(); else start();
        });
      });

      root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); stop(); go(-1); announce(); start(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); stop(); go(1);  announce(); start(); }
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);

      let startX = 0;
      let isTouching = false;
      track.addEventListener('touchstart', (e) => {
        isTouching = true; startX = e.touches[0].clientX; stop();
      }, { passive: true });
      track.addEventListener('touchend', (e) => {
        if (!isTouching) return;
        const delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
        isTouching = false;
        start();
      });

      show(index);
      start();
    });
  }

  /* ----------------------------------------------------------
     SLIDE-OUT CART
  ---------------------------------------------------------- */
  function initCart() {
    // Open cart triggers. The header trigger is an <a href="/cart"> so
    // the cart stays reachable without JavaScript; with JS we upgrade
    // it to open the drawer instead.
    document.querySelectorAll('.header__cart-btn, [data-open-cart]').forEach(
      (btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          Cart.openDrawer();
        });
      }
    );

    // Close cart
    const closeBtn = document.querySelector('.slide-cart__close');
    if (closeBtn) closeBtn.addEventListener('click', () => Cart.closeDrawer());

    const overlay = document.querySelector('.cart-overlay');
    if (overlay)
      overlay.addEventListener('click', () => Cart.closeDrawer());

    // Escape key for cart
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const cart = document.querySelector('.slide-cart');
        if (cart && cart.classList.contains('is-open')) {
          Cart.closeDrawer();
        }
      }
    });

    // Continue shopping
    const continueBtn = document.querySelector('.slide-cart__continue');
    if (continueBtn)
      continueBtn.addEventListener('click', () => Cart.closeDrawer());

    // Checkout button
    const checkoutBtn = document.querySelector('.slide-cart__footer .btn--add-to-cart, [data-checkout]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        window.location.href = window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + 'checkout' : '/checkout';
      });
    }

    // Quick-add buttons on product cards. Delegated so cards rendered after
    // load (filtered grids, editor section reloads) work without re-binding.
    // Cards carry data-variant-id rendered by the purluv-product-card snippet.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn || btn.closest('.product-info')) return;
      e.preventDefault();
      const card = btn.closest('[data-product]') || btn.closest('.product-card');
      if (!card) return;

      const variantId = card.dataset.variantId;
      const name = card.dataset.productName || card.querySelector('.product-card__title a, .product-card__title')?.textContent?.trim() || 'Pur Luv Treat';

      // Button feedback. Icon-only quick-add buttons (the orange "+" on
      // product cards) just toggle a class so the plus swaps to a check;
      // text buttons swap their label.
      if (btn.classList.contains('product-card__add')) {
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1800);
      } else {
        const origText = btn.textContent;
        btn.textContent = 'Added! \u2713';
        btn.classList.add('is-added');
        setTimeout(() => {
          btn.textContent = origText;
          btn.classList.remove('is-added');
        }, 2000);
      }

      Cart.add(variantId, 1, { name: name });
    });

    // Initial render - hydrate from the live Shopify cart.
    Cart.refresh();
  }

  /* ----------------------------------------------------------
     SCROLL-TRIGGERED COUNTERS + COMPARISON BARS
  ---------------------------------------------------------- */

  /* ----------------------------------------------------------
     PDP STICKY ADD-TO-CART BAR
     Built from the live product DOM so it reuses the main
     add-to-cart logic (variant, qty, price). Only runs on PDPs.
  ---------------------------------------------------------- */
  function initStickyBuyBar() {
    return; /* sticky bar hidden for now — delete this line to restore */
    const info = document.querySelector('.product-info');
    const mainBtn = document.querySelector('.product-info .btn--add-to-cart');
    if (!info || !mainBtn) return;

    const title = document.querySelector('.product-info__title');
    const price = document.querySelector('.product-info__price');
    const img = document.querySelector('.product-gallery__main img');
    const variantBtn = document.querySelector('.variant-selector__btn.is-active');

    // Meta line = the selected variant only (no rating).
    const variant = variantBtn ? variantBtn.textContent.trim() : '';
    const meta = variant;

    const bar = document.createElement('div');
    bar.className = 'sticky-buy';
    bar.id = 'sticky-buy';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML =
      '<div class="sticky-buy__inner">' +
        '<img class="sticky-buy__img" src="' + (img ? img.src : '') + '" alt="" aria-hidden="true">' +
        '<div class="sticky-buy__info">' +
          '<span class="sticky-buy__title">' + (title ? title.textContent.trim() : 'Pur Luv Treat') + '</span>' +
          (meta ? '<span class="sticky-buy__meta">' + meta + '</span>' : '') +
        '</div>' +
        '<div class="sticky-buy__action">' +
          '<span class="sticky-buy__price">' + (price ? price.textContent.trim() : '') + '</span>' +
          '<button class="btn btn--add-to-cart sticky-buy__btn" type="button" aria-label="Add to cart">Add to Cart</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(bar);

    // Reuse the main button's full add logic (variant/qty/price) via a click.
    const stickyBtn = bar.querySelector('.sticky-buy__btn');
    stickyBtn.addEventListener('click', () => {
      mainBtn.click();
      const orig = stickyBtn.textContent;
      stickyBtn.textContent = 'Added! \u2713';
      setTimeout(() => { stickyBtn.textContent = orig; }, 1600);
    });

    // Show the bar once the main buy row has scrolled above the viewport.
    const sentinel = document.querySelector('.product-info__buy') || mainBtn;
    bar.inert = true; // hidden at load — keep its button unfocusable

    let shown = false;
    function update() {
      const show = sentinel.getBoundingClientRect().bottom < 8;
      if (show === shown) return;
      shown = show;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.inert = !show;
    }
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(() => { update(); ticking = false; }); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ----------------------------------------------------------
     QUICK VIEW MODAL
     Opens a minimal popup from a product card so shoppers can add
     to cart without leaving the page.
  ---------------------------------------------------------- */
  function initQuickView() {
    const modal = document.getElementById('quickview');
    const overlay = document.getElementById('quickview-overlay');
    if (!modal || !overlay) return;

    const elImage = document.getElementById('quickview-image');
    const elMeta = document.getElementById('quickview-meta');
    const elTitle = document.getElementById('quickview-title');
    const elDesc = document.getElementById('quickview-desc');
    const elPrice = document.getElementById('quickview-price');
    const elLink = document.getElementById('quickview-link');
    const elAdd = document.getElementById('quickview-add');

    let current = null;
    let lastFocused = null;

    function open(card) {
      current = {
        id: card.dataset.productId || '',
        variantId: card.dataset.variantId || '',
        name: card.dataset.productName || card.querySelector('.product-card__title a, .product-card__title')?.textContent?.trim() || 'Pur Luv Treat',
        price: card.dataset.productPrice || card.querySelector('.product-card__price')?.textContent?.trim() || '',
        image: card.dataset.productImage || card.querySelector('.product-card__image')?.src || '',
        variant: card.dataset.productVariant || '',
      };

      const meta = card.querySelector('.product-card__meta')?.textContent?.trim() || current.variant;
      const desc = card.querySelector('.product-card__description')?.textContent?.trim() || '';
      const href = card.dataset.productUrl || card.querySelector('.product-card__title a, a[href]')?.getAttribute('href') || '#';

      if (elImage) { elImage.src = current.image; elImage.alt = current.name; }
      if (elMeta) elMeta.textContent = meta;
      if (elTitle) elTitle.textContent = current.name.replace(/^Pur Luv\s+/i, '');
      if (elDesc) elDesc.textContent = desc;
      if (elPrice) elPrice.textContent = current.price;
      if (elLink) elLink.setAttribute('href', href);

      lastFocused = document.activeElement;
      overlay.classList.add('is-open');
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      modal.inert = false;
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (elAdd) elAdd.focus();
    }

    function close() {
      overlay.classList.remove('is-open');
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      modal.inert = true;
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    // Closed at load — unreachable for keyboard and screen readers.
    modal.inert = true;

    // Open triggers — delegated so dynamically rendered cards work too.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-view]');
      if (!btn) return;
      e.preventDefault();
      const card = btn.closest('.product-card');
      if (card) open(card);
    });

    // Close triggers
    document.querySelectorAll('[data-action="close-quickview"]').forEach((el) => {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    // Add to cart from modal
    if (elAdd) {
      elAdd.addEventListener('click', () => {
        if (!current) return;
        close();
        Cart.add(current.variantId, 1, { name: current.name });
      });
    }
  }

  /* ----------------------------------------------------------
     PRODUCT PAGE - Image Gallery
  ---------------------------------------------------------- */
  function initProductGallery() {
    const mainImage = document.querySelector('.product-gallery__main img');
    const thumbs = document.querySelectorAll('.product-gallery__thumb');
    const mainWrap = document.querySelector('.product-gallery__main');

    if (!mainImage || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const img = thumb.querySelector('img');
        if (!img) return;
        const src = thumb.dataset.image || img.src;
        const alt = thumb.dataset.alt || img.alt || '';
        mainImage.src = src;
        mainImage.alt = alt;
        thumbs.forEach((t) => {
          t.classList.remove('is-active');
          t.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-pressed', 'true');
        // Lifestyle photos (jpg) fill the frame; product cutouts (png) get padding
        if (mainWrap) {
          const isLifestyle = /\.(jpe?g|webp|avif)(\?|$)/i.test(src);
          mainWrap.classList.toggle('product-gallery__main--lifestyle', isLifestyle);
        }
        // Update magnifier background when image changes
        if (lens) {
          lens.style.backgroundImage = 'url(' + src + ')';
        }
      });

      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          thumb.click();
        }
      });
    });

    // Lightbox on click
    if (mainWrap) {
      mainWrap.addEventListener('click', (e) => {
        // Don't open lightbox if magnifier is active (user is zooming)
        if (e.target.classList.contains('magnifier-lens')) return;
        Lightbox.open(mainImage.src, mainImage.alt);
      });
    }

    // ---- MAGNIFIER ----
    const lens = mainWrap && mainWrap.querySelector('.magnifier-lens');
    if (!lens || !mainWrap) return;

    const ZOOM = 2.5; // zoom factor
    const LENS_SIZE = 180;

    function updateMagnifier(e) {
      // Use the image's rect so padding on the wrap doesn't distort the zoom
      var wrapRect = mainWrap.getBoundingClientRect();
      var imgRect = mainImage.getBoundingClientRect();

      // Lens position is relative to the wrap (its positioning parent)
      var lensX = e.clientX - wrapRect.left;
      var lensY = e.clientY - wrapRect.top;
      lens.style.left = (lensX - LENS_SIZE / 2) + 'px';
      lens.style.top = (lensY - LENS_SIZE / 2) + 'px';

      // Background coords are relative to the image, not the wrap
      var imgX = e.clientX - imgRect.left;
      var imgY = e.clientY - imgRect.top;
      imgX = Math.max(0, Math.min(imgX, imgRect.width));
      imgY = Math.max(0, Math.min(imgY, imgRect.height));

      var bgW = imgRect.width * ZOOM;
      var bgH = imgRect.height * ZOOM;
      var bgX = -(imgX * ZOOM - LENS_SIZE / 2);
      var bgY = -(imgY * ZOOM - LENS_SIZE / 2);

      lens.style.backgroundSize = bgW + 'px ' + bgH + 'px';
      lens.style.backgroundPosition = bgX + 'px ' + bgY + 'px';
    }

    mainWrap.addEventListener('mouseenter', function () {
      lens.style.backgroundImage = 'url(' + mainImage.src + ')';
      lens.classList.add('is-active');
      mainWrap.style.cursor = 'none';
    });

    mainWrap.addEventListener('mousemove', function (e) {
      if (!lens.classList.contains('is-active')) return;
      requestAnimationFrame(function () {
        updateMagnifier(e);
      });
    });

    mainWrap.addEventListener('mouseleave', function () {
      lens.classList.remove('is-active');
      mainWrap.style.cursor = 'zoom-in';
    });

    // Keyboard: Enter opens lightbox (magnifier is mouse-only)
    mainWrap.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        Lightbox.open(mainImage.src, mainImage.alt);
      }
    });
  }

  /* ----------------------------------------------------------
     LIGHTBOX
  ---------------------------------------------------------- */
  const Lightbox = {
    el: null,
    images: [],
    currentIndex: 0,

    init() {
      this.el = document.querySelector('.lightbox');
      if (!this.el) return;
      this.el.inert = true; // closed at load

      const closeBtn = this.el.querySelector('.lightbox__close');
      const prevBtn = this.el.querySelector('.lightbox__nav--prev');
      const nextBtn = this.el.querySelector('.lightbox__nav--next');

      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
      if (nextBtn) nextBtn.addEventListener('click', () => this.next());

      this.el.addEventListener('click', (e) => {
        if (e.target === this.el) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (!this.el.classList.contains('is-open')) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });

      // Collect images from gallery thumbnails
      const thumbs = document.querySelectorAll('.product-gallery__thumb img');
      this.images = Array.from(thumbs).map((img) => ({
        src: img.src,
        alt: img.alt,
      }));
    },

    lastFocused: null,

    open(src, alt) {
      if (!this.el) return;
      const img = this.el.querySelector('.lightbox__image');
      if (img) {
        img.src = src;
        img.alt = alt || '';
      }
      this.currentIndex = this.images.findIndex((i) => i.src === src);
      this.lastFocused = document.activeElement;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      this.el.inert = false;
      document.body.style.overflow = 'hidden';
      const closeBtn = this.el.querySelector('.lightbox__close');
      if (closeBtn) closeBtn.focus();
    },

    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      this.el.inert = true;
      document.body.style.overflow = '';
      if (this.lastFocused && this.lastFocused.focus && document.contains(this.lastFocused)) {
        this.lastFocused.focus();
      }
      this.lastFocused = null;
    },

    prev() {
      if (this.images.length < 2) return;
      this.currentIndex =
        (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.showCurrent();
    },

    next() {
      if (this.images.length < 2) return;
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
      this.showCurrent();
    },

    showCurrent() {
      const img = this.el.querySelector('.lightbox__image');
      const data = this.images[this.currentIndex];
      if (img && data) {
        img.src = data.src;
        img.alt = data.alt;
      }
    },
  };

  /* ----------------------------------------------------------
     PRODUCT PAGE - Tabs
  ---------------------------------------------------------- */
  function initProductTabs() {
    const tabs = document.querySelectorAll('.product-tabs__tab');
    const panels = document.querySelectorAll('.product-tabs__panel');

    if (!tabs.length) return;

    // Roving tabindex: one tab stop for the tablist, arrows move within.
    tabs.forEach((t) => {
      t.setAttribute('tabindex', t.classList.contains('is-active') ? '0' : '-1');
    });

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.getAttribute('aria-controls') || tab.dataset.tab;

        tabs.forEach((t) => {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });
        tab.setAttribute('tabindex', '0');
        panels.forEach((p) => {
          p.classList.remove('is-active');
          p.setAttribute('aria-hidden', 'true');
        });

        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');

        const panel = target ? document.getElementById(target) : null;
        if (panel) {
          panel.classList.add('is-active');
          panel.setAttribute('aria-hidden', 'false');
        }
      });

      // Keyboard arrow navigation
      tab.addEventListener('keydown', (e) => {
        const tabArr = Array.from(tabs);
        const idx = tabArr.indexOf(tab);
        let newIdx;

        if (e.key === 'ArrowRight') {
          e.preventDefault();
          newIdx = (idx + 1) % tabArr.length;
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          newIdx = (idx - 1 + tabArr.length) % tabArr.length;
        } else if (e.key === 'Home') {
          e.preventDefault();
          newIdx = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          newIdx = tabArr.length - 1;
        }

        if (newIdx !== undefined) {
          tabArr[newIdx].focus();
          tabArr[newIdx].click();
        }
      });
    });
  }

  /* ----------------------------------------------------------
     PRODUCT PAGE - Variant Selector
  ---------------------------------------------------------- */
  function initVariantSelector() {
    const buttons = document.querySelectorAll('.variant-selector__btn');
    if (!buttons.length) return;

    const btnArr = Array.from(buttons);

    // Radio-group keyboard semantics: roving tabindex + arrow keys.
    const syncTabindex = () => {
      btnArr.forEach((b) => {
        b.setAttribute('tabindex', b.classList.contains('is-active') ? '0' : '-1');
      });
    };
    syncTabindex();

    btnArr.forEach((btn) => {
      btn.addEventListener('keydown', (e) => {
        let dir = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
        if (!dir) return;
        e.preventDefault();
        const next = btnArr[(btnArr.indexOf(btn) + dir + btnArr.length) % btnArr.length];
        next.focus();
        next.click();
      });
    });

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        buttons.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');
        syncTabindex();

        // Sync price, compare-at price, availability and URL to the
        // selected variant (data attributes rendered by the PDP section).
        const price = document.querySelector('.product-info__price');
        if (price && btn.dataset.price) price.textContent = btn.dataset.price;
        const compare = document.querySelector('.product-info__compare-price');
        if (compare) {
          if (btn.dataset.comparePrice) {
            compare.textContent = btn.dataset.comparePrice;
            compare.style.display = '';
          } else {
            compare.style.display = 'none';
          }
        }
        const addBtn = document.querySelector('.product-info .btn--add-to-cart');
        if (addBtn && btn.dataset.available !== undefined) {
          const available = btn.dataset.available !== 'false';
          addBtn.disabled = !available;
          if (available) {
            addBtn.textContent = addBtn.dataset.labelAdd || 'Add to Cart';
          } else {
            addBtn.textContent = addBtn.dataset.labelSoldOut || 'Sold Out';
          }
        }
        // Keep the no-JS fallback form in sync too.
        const idInput = document.querySelector('.product-info form input[name="id"]');
        if (idInput && btn.dataset.variantId) idInput.value = btn.dataset.variantId;
        if (btn.dataset.variantId && window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', btn.dataset.variantId);
          window.history.replaceState({}, '', url.toString());
        }
        // Keep the sticky buy bar price in sync.
        const stickyPrice = document.querySelector('.sticky-buy__price');
        if (stickyPrice && btn.dataset.price) stickyPrice.textContent = btn.dataset.price;
      });
    });
  }

  /* ----------------------------------------------------------
     PRODUCT PAGE - Description placement
     Wide screens: under the gallery (image, thumbnails, copy). On
     phones the two columns stack, so the copy moves into the buy box
     after the form (add button, Buy with Prime) and above the trust
     line, and back again when the viewport widens.
  ---------------------------------------------------------- */
  function initProductDescriptionPlacement() {
    const desc = document.querySelector('.product-gallery__desc');
    const gallery = document.querySelector('.product-gallery');
    const info = document.querySelector('.product-info');
    if (!desc || !gallery || !info) return;
    const mq = window.matchMedia('(max-width: 900px)');
    const trust = info.querySelector('.product-info__trust');
    const place = () => {
      if (mq.matches) {
        if (trust) { if (desc.nextElementSibling !== trust) info.insertBefore(desc, trust); }
        else if (desc.parentNode !== info) info.appendChild(desc);
      } else if (desc.parentNode !== gallery) {
        gallery.appendChild(desc);
      }
    };
    place();
    if (mq.addEventListener) mq.addEventListener('change', place);
    else mq.addListener(place);
  }

  /* ----------------------------------------------------------
     PRODUCT PAGE - Quantity Selector
  ---------------------------------------------------------- */
  function initQuantitySelector() {
    document.querySelectorAll('.quantity-selector').forEach((selector) => {
      const decreaseBtn = selector.querySelector('[data-qty-decrease]');
      const increaseBtn = selector.querySelector('[data-qty-increase]');
      const valueEl = selector.querySelector('.quantity-selector__value');

      if (!decreaseBtn || !increaseBtn || !valueEl) return;

      // On the PDP the form submits a hidden quantity input that the
      // subscription widget also writes to (each plan's bag count; cleared
      // for a one-time purchase). Mirror it with the visible stepper in
      // both directions. A hidden input's value is its attribute, so the
      // widget's writes show up as attribute mutations.
      const form = selector.closest('form');
      const field = form && form.querySelector('input[type="hidden"][name="quantity"]');
      if (field) {
        const fromField = () => {
          const qty = parseInt(field.value, 10);
          valueEl.value = qty > 0 ? Math.min(99, qty) : 1;
        };
        new MutationObserver(fromField).observe(field, { attributes: true, attributeFilter: ['value'] });
        if (field.value !== valueEl.value) fromField();
      }

      // The stepper is only offered for a one-time purchase. The widget keeps
      // its selected plan in a hidden selling_plan input it prepends to the
      // form (empty = one-time); a plan fixes the bag count, so the stepper
      // hides while one is selected and comes back at 1 for one-time. A
      // product with plans renders the stepper hidden so it does not flash
      // before the widget mounts; if no widget shows up, it is revealed.
      const qtyWrap = selector.closest('.product-info__qty');
      if (form && qtyWrap) {
        const pending = qtyWrap.hidden;
        let hadPlan = null;
        const syncPlan = (force) => {
          const planInput = form.querySelector('[name="selling_plan"]');
          if (!planInput && pending && !force && hadPlan === null) return;
          const hasPlan = !!(planInput && planInput.value);
          qtyWrap.hidden = hasPlan;
          if (hadPlan && !hasPlan) updateQty(1);
          hadPlan = hasPlan;
        };
        new MutationObserver(() => syncPlan(false))
          .observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
        syncPlan(false);
        if (pending) setTimeout(() => syncPlan(true), 4000);
      }

      function updateQty(newVal) {
        const val = Math.max(1, Math.min(99, newVal));
        valueEl.value = val;
        if (field) field.value = val;

        if (newVal < 1) {
          selector.style.animation = 'none';
          selector.offsetHeight; // Trigger reflow
          selector.style.animation = null;
          selector.classList.add('is-shaking');
          setTimeout(() => selector.classList.remove('is-shaking'), 400);
        }
      }

      decreaseBtn.addEventListener('click', () => {
        updateQty(parseInt(valueEl.value) - 1);
      });

      increaseBtn.addEventListener('click', () => {
        updateQty(parseInt(valueEl.value) + 1);
      });

      valueEl.addEventListener('change', () => {
        updateQty(parseInt(valueEl.value) || 1);
      });
    });
  }

  /* ----------------------------------------------------------
     PRODUCT PAGE - Subscription plan
     The subscription widget (app block) keeps the plan it has
     selected in a hidden selling_plan input it adds to the product
     form; empty or missing means a one-time purchase.
  ---------------------------------------------------------- */
  function pdpSellingPlan() {
    const input = document.querySelector('.product-info form [name="selling_plan"]');
    return input ? input.value : '';
  }

  /* ----------------------------------------------------------
     PRODUCT PAGE - Add to Cart (Detail Page)
  ---------------------------------------------------------- */
  function initProductPageCart() {
    const addBtn = document.querySelector('.product-info .btn--add-to-cart');
    if (!addBtn) return;

    addBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;

      const title = document.querySelector('.product-info__title');
      const activeVariant = document.querySelector('.variant-selector__btn.is-active');
      const qtyInput = document.querySelector('.quantity-selector__value');

      // Variant id: active variant button first, then the section's own
      // data attribute (single-variant products render no selector).
      const info = document.querySelector('.product-info');
      const variantId = (activeVariant && activeVariant.dataset.variantId) ||
        (info && info.dataset.variantId) || '';
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      // Button feedback
      const origText = addBtn.textContent;
      addBtn.textContent = 'Added! \u2713';
      addBtn.classList.add('is-added');
      setTimeout(() => {
        addBtn.textContent = origText;
        addBtn.classList.remove('is-added');
      }, 2000);

      Cart.add(variantId, qty, {
        name: title ? title.textContent.trim() : 'Pur Luv Treat',
        sellingPlan: pdpSellingPlan(),
      });
    });
  }

  /* ----------------------------------------------------------
     COLLECTION PAGE - Filters
  ---------------------------------------------------------- */
  function initFilters() {
    const filterOptions = document.querySelectorAll('.filter-option');
    filterOptions.forEach((option) => {
      // Options that wrap a real <input type="checkbox"> (the Shopify
      // storefront-filter form) get their semantics, keyboard handling
      // and state from the native input — no ARIA emulation needed.
      if (option.querySelector('input[type="checkbox"], input[type="number"]')) return;

      option.addEventListener('click', () => {
        option.classList.toggle('is-active');
        const checkbox = option.querySelector('.filter-option__checkbox');
        const isActive = option.classList.contains('is-active');
        option.setAttribute('aria-checked', isActive);
        if (checkbox) {
          checkbox.setAttribute('aria-checked', isActive);
        }
      });

      // Keyboard
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          option.click();
        }
      });
    });

    // Mobile filter toggle
    const filterToggle = document.querySelector('.filter-toggle');
    const filterSidebar = document.querySelector('.filter-sidebar');
    const filterClose = document.querySelector('.filter-sidebar__close');

    if (filterToggle && filterSidebar) {
      filterToggle.addEventListener('click', () => {
        filterSidebar.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        if (filterClose) filterClose.focus();
      });
    }

    if (filterClose && filterSidebar) {
      filterClose.addEventListener('click', () => {
        filterSidebar.classList.remove('is-open');
        document.body.style.overflow = '';
        if (filterToggle) filterToggle.focus();
      });
    }
  }

  /* ----------------------------------------------------------
     TESTIMONIAL SLIDER
  ---------------------------------------------------------- */
  function initTestimonials() {
    const cards = document.querySelectorAll('.testimonial-card');
    const dots = document.querySelectorAll('.testimonials__dot');

    if (cards.length < 2) return;

    let current = 0;

    function showSlide(index) {
      cards.forEach((c) => {
        c.style.display = 'none';
        c.setAttribute('aria-hidden', 'true');
      });
      dots.forEach((d) => d.classList.remove('is-active'));

      cards[index].style.display = 'block';
      cards[index].setAttribute('aria-hidden', 'false');
      if (dots[index]) dots[index].classList.add('is-active');
      current = index;
    }

    showSlide(0);

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => showSlide(i));
    });

    // Auto-advance
    let autoPlay = setInterval(() => {
      showSlide((current + 1) % cards.length);
    }, 6000);

    // Pause on hover
    const slider = document.querySelector('.testimonials__slider');
    if (slider) {
      slider.addEventListener('mouseenter', () => clearInterval(autoPlay));
      slider.addEventListener('mouseleave', () => {
        autoPlay = setInterval(() => {
          showSlide((current + 1) % cards.length);
        }, 6000);
      });
    }
  }

  /* ----------------------------------------------------------
     SCROLL ANIMATIONS
  ---------------------------------------------------------- */
  function initScrollAnimations() {
    const elements = document.querySelectorAll(
      '.animate-on-scroll, .animate-on-scroll--left, .animate-on-scroll--right, .animate-on-scroll--scale, .stagger-children'
    );

    if (!elements.length) return;

    // Check reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ----------------------------------------------------------
     HEADER SCROLL BEHAVIOR
  ---------------------------------------------------------- */
  function initHeaderScroll() {
    const header = document.querySelector('.header');
    if (!header) return;

    let lastScroll = 0;
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScroll = window.scrollY;

          if (currentScroll > 200 && currentScroll > lastScroll) {
            header.classList.add('is-hidden');
          } else {
            header.classList.remove('is-hidden');
          }

          lastScroll = currentScroll;
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /* ----------------------------------------------------------
     KEYBOARD TRAP FOR MODALS
  ---------------------------------------------------------- */
  function trapFocus(element) {
    const focusable = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    element.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  }

  /* ----------------------------------------------------------
     INIT FOCUS TRAPS
  ---------------------------------------------------------- */
  function initFocusTraps() {
    const slideCart = document.querySelector('.slide-cart');
    if (slideCart) trapFocus(slideCart);

    const mobileMenu = document.querySelector('.mobile-menu');
    if (mobileMenu) trapFocus(mobileMenu);

    const lightbox = document.querySelector('.lightbox');
    if (lightbox) trapFocus(lightbox);
  }

  /* ----------------------------------------------------------
     NEWSLETTER FORM
  ---------------------------------------------------------- */
  function initNewsletter() {
    document.querySelectorAll('.newsletter__form, .footer__newsletter-form').forEach((form) => {
      // Real Shopify customer forms (rendered with {% form 'customer' %})
      // submit natively; success/error toasts fire on the reloaded page.
      if (form.hasAttribute('data-shopify-form')) return;
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        if (input && input.value) {
          Toast.show('Thanks for subscribing! Share the luv!', 'success');
          input.value = '';
        }
      });
    });

    // Post-submit feedback after Shopify handles the customer form.
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true') {
      Toast.show('Thanks for subscribing! Share the luv!', 'success');
    }
  }

  /* ----------------------------------------------------------
     COLLECTION FILTER FORM + SORT
     Progressive enhancement over Shopify storefront filtering:
     any change inside [data-filter-form] resubmits the form, and
     [data-sort-select] rewrites the sort_by param in place.
  ---------------------------------------------------------- */
  function initFilterForm() {
    document.querySelectorAll('[data-filter-form]').forEach((form) => {
      form.addEventListener('change', () => {
        // Visual pressed state matches the static site before navigation.
        form.classList.add('is-loading');
        form.submit();
      });
    });

    document.querySelectorAll('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  /* ----------------------------------------------------------
     PRODUCT CAROUSEL
     Lightweight slide carousel. Supports next/prev, dots,
     keyboard nav, and swipe.
  ---------------------------------------------------------- */
  function initCarousel() {
    const carousels = document.querySelectorAll('[data-carousel]');
    if (!carousels.length) return;

    carousels.forEach((root) => {
      const track = root.querySelector('[data-carousel-track]');
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      const dotsWrap = root.querySelector('[data-carousel-dots]');
      if (!track) return;

      const slides = Array.from(track.children);
      let index = 0;

      function slidesPerView() {
        const w = window.innerWidth;
        if (w <= 600) return 1;
        if (w <= 900) return 2;
        return 3;
      }

      function maxIndex() {
        return Math.max(0, slides.length - slidesPerView());
      }

      function update() {
        if (!slides.length) return;
        const first = slides[0].getBoundingClientRect();
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 24;
        const offset = index * (first.width + gap);
        track.style.transform = 'translateX(' + (-offset) + 'px)';

        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= maxIndex();

        if (dotsWrap) {
          const dots = dotsWrap.querySelectorAll('.carousel__dot');
          dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
        }
      }

      function renderDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        const count = maxIndex() + 1;
        for (let i = 0; i < count; i++) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'carousel__dot' + (i === index ? ' is-active' : '');
          btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          btn.addEventListener('click', () => { index = i; update(); });
          dotsWrap.appendChild(btn);
        }
      }

      function go(dir) {
        index = Math.max(0, Math.min(maxIndex(), index + dir));
        update();
      }

      if (prev) prev.addEventListener('click', () => go(-1));
      if (next) next.addEventListener('click', () => go(1));

      // Keyboard
      root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      });

      // Swipe
      let startX = 0;
      let isTouching = false;
      track.addEventListener('touchstart', (e) => {
        isTouching = true; startX = e.touches[0].clientX;
      }, { passive: true });
      track.addEventListener('touchend', (e) => {
        if (!isTouching) return;
        const endX = e.changedTouches[0].clientX;
        const delta = endX - startX;
        if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
        isTouching = false;
      });

      // Resize
      let resizeTO;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTO);
        resizeTO = setTimeout(() => {
          index = Math.min(index, maxIndex());
          renderDots();
          update();
        }, 120);
      });

      renderDots();
      update();
    });
  }

  /* ----------------------------------------------------------
     MINI MEGA MENU
     Auto-wires any `a[href="collection.html"]` inside header__nav
     into a 3-column dropdown (proteins / shop / featured).
     Hover open with 150ms close delay. Click toggles on desktop,
     navigates normally on mobile. Escape + outside-click close.
  ---------------------------------------------------------- */
  function initMegaMenu() {
    // The mega panel is server-rendered HTML and opens via CSS :hover /
    // :focus-within, so it works even if this script never runs. JS only
    // syncs aria-expanded for assistive tech and closes on Escape.
    document.querySelectorAll('.has-mega').forEach((wrap) => {
      const trigger = wrap.querySelector('.header__nav-link--mega');
      if (!trigger) return;
      const set = (v) => trigger.setAttribute('aria-expanded', String(v));
      wrap.addEventListener('mouseenter', () => set(true));
      wrap.addEventListener('mouseleave', () => set(false));
      wrap.addEventListener('focusin', () => set(true));
      wrap.addEventListener('focusout', (e) => {
        if (!wrap.contains(e.relatedTarget)) set(false);
      });
      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && wrap.contains(document.activeElement)) {
          document.activeElement.blur();
        }
      });
    });
  }

  /* ----------------------------------------------------------
     IMAGE SKELETON REVEAL
     Any <img> inside [data-skeleton="true"] fades in when loaded.
     Keeps the shimmer visible behind the image until it resolves.
  ---------------------------------------------------------- */
  function initSkeletonReveal() {
    const wraps = document.querySelectorAll('.image-wrap[data-skeleton="true"]');
    wraps.forEach((wrap) => {
      const img = wrap.querySelector('img');
      if (!img) return;
      const reveal = () => img.classList.add('is-loaded');
      if (img.complete && img.naturalWidth > 0) {
        reveal();
      } else {
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener('error', reveal, { once: true });
      }
    });
  }

  /* ----------------------------------------------------------
     INIT EVERYTHING
  ---------------------------------------------------------- */
  function init() {
    initAnnouncementBar();
    initMobileMenu();
    initMegaMenu();
    initCart();
    initQuickView();
    initDelights();
    initProductGallery();
    Lightbox.init();
    initProductTabs();
    initVariantSelector();
    initQuantitySelector();
    initProductDescriptionPlacement();
    initProductPageCart();
    initStickyBuyBar();
    initFilters();
    initTestimonials();
    initCarousel();
    initPlCarousel();
    initSkeletonReveal();
    initScrollAnimations();
    initHeaderScroll();
    initFocusTraps();
    initNewsletter();
    initFilterForm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   PL SNAP — scroll-snap carousel init (best sellers, reviews)
   ============================================================ */
(function () {
  'use strict';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.querySelectorAll('[data-pl-snap]').forEach(function (root) {
    var track = root.querySelector('[data-pl-snap-track]');
    var prev = root.querySelector('[data-pl-snap-prev]');
    var next = root.querySelector('[data-pl-snap-next]');
    if (!track || !prev || !next) return;
    function step() {
      var card = track.firstElementChild;
      if (!card) return 0;
      var gap = parseFloat(getComputedStyle(track).columnGap || '0');
      return card.getBoundingClientRect().width + gap;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth - 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max;
    }
    prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduced ? 'auto' : 'smooth' }); });
    next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduced ? 'auto' : 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });
})();

/* ============================================================
   REVIEW CLAMP + READ MORE — clamp long review text (CSS
   .is-clamped, 8 lines) and add a toggle only when it overflows.
   Progressive: without JS the full text simply shows.
   ============================================================ */
(function () {
  function initReviewClamp() {
    document.querySelectorAll('.review .review__text').forEach(function (el) {
      if (el.dataset.clampInit) return;
      el.dataset.clampInit = '1';
      el.classList.add('is-clamped');
      if (el.scrollHeight <= el.clientHeight + 2) {
        el.classList.remove('is-clamped');
        return;
      }
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'review__more';
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = 'Read More';
      el.insertAdjacentElement('afterend', btn);
      btn.addEventListener('click', function () {
        var expanded = !el.classList.contains('is-clamped');
        if (expanded) {
          el.classList.add('is-clamped');
          btn.setAttribute('aria-expanded', 'false');
          btn.textContent = 'Read More';
        } else {
          el.classList.remove('is-clamped');
          btn.setAttribute('aria-expanded', 'true');
          btn.textContent = 'Read Less';
        }
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReviewClamp);
  } else {
    initReviewClamp();
  }
})();

/* ============================================================
   AJ CAMPAIGN FILM LIGHTBOX — the LP hero film AUTOPLAYS
   MUTED inline in the hero frame
   (.ajx-vhero__inline); the Watch Now button opens the modal
   <video>, which restarts from 0 WITH SOUND while the inline
   copy pauses. Close = backdrop, X, or Escape — pauses the
   modal, resumes the muted inline loop, returns focus. Inline
   autoplay is skipped under prefers-reduced-motion (CSS hides
   it, the poster still shows). Null-safe on pages without it.
   ============================================================ */
(function () {
  function init() {
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var inline = document.querySelector('.ajx-vhero__inline');
    if (inline && reduceMotion) inline.pause();
    var modal = document.querySelector('[data-ajx-vmodal]');
    if (!modal) return;
    var video = modal.querySelector('video');
    var closeBtn = modal.querySelector('.ajx-vmodal__close');
    var opener = null;
    function open(trigger) {
      opener = trigger || null;
      modal.hidden = false;
      document.body.classList.add('ajx-vmodal-open');
      if (closeBtn) closeBtn.focus();
      if (inline) inline.pause();
      if (video) {
        video.currentTime = 0;
        video.muted = false;
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
    }
    function close() {
      modal.hidden = true;
      document.body.classList.remove('ajx-vmodal-open');
      if (video) video.pause();
      if (inline && !reduceMotion) {
        var r = inline.play();
        if (r && r.catch) r.catch(function () {});
      }
      if (opener && opener.focus) opener.focus();
      opener = null;
    }
    document.querySelectorAll('[data-ajx-video]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open(btn);
      });
    });
    modal.querySelectorAll('[data-ajx-vmodal-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   REVIEWS VIEW-MORE TOGGLE — the homepage "Dig Into More
   Reviews" control is a disclosure button: click
   reveals the 3 extra .review--extra cards (second row on the
   desktop grid, extra slides in the mobile carousel) and the
   arrow flips from down to up. Null-safe on pages without it.
   ============================================================ */
(function () {
  function init() {
    document.querySelectorAll('[data-reviews-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var section = btn.closest('.reviews');
        if (!section) return;
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        section.querySelectorAll('.review--extra').forEach(function (card) {
          card.hidden = expanded;
        });
        btn.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   AJ LP GUILT CAROUSEL MOTION — mobile only (<=900px); desktop
   is a plain manual snap row (all four tiles fit once they are
   capped, so there is nothing to scroll). On mobile the snap
   carousel steps one tile every 2.5s, loops to the first, only while the track
   is >=40% on screen in a visible tab; never moves under
   prefers-reduced-motion; touch/drag/wheel/manual scroll holds
   the timer 8s so it never fights the user's thumb.
   ============================================================ */
(function () {
  'use strict';
  var STEP_MS = 2500;
  var HOLD_MS = 8000;
  function init() {
    var track = document.querySelector('.ajx-guilt__tiles');
    if (!track) return;
    var mobile = window.matchMedia('(max-width: 900px)');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var inView = false;
    var holdUntil = 0;
    var programmatic = false;
    var progTimer = null;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
      }, { threshold: 0.4 }).observe(track);
    } else {
      inView = true;
    }
    function hold() { holdUntil = Date.now() + HOLD_MS; }
    ['touchstart', 'pointerdown', 'wheel'].forEach(function (ev) {
      track.addEventListener(ev, hold, { passive: true });
    });
    track.addEventListener('scroll', function () {
      if (!programmatic) hold();
    }, { passive: true });
    var last = Date.now();
    setInterval(function () {
      var now = Date.now();
      var eligible = mobile.matches && !reduced.matches && inView && !document.hidden && now >= holdUntil;
      if (!eligible) { last = now; return; }
      if (now - last < STEP_MS) return;
      last = now;
      var card = track.firstElementChild;
      if (!card) return;
      var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      var step = card.getBoundingClientRect().width + gap;
      var max = track.scrollWidth - track.clientWidth - 2;
      programmatic = true;
      clearTimeout(progTimer);
      progTimer = setTimeout(function () { programmatic = false; }, 1200);
      if (track.scrollLeft >= max) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: step, behavior: 'smooth' });
      }
    }, 250);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   RECHARGE BADGE ACCENT — the subscription widget's "Most Popular" /
   "Best value" badges take the PDP family accent (--pdp-deep, the
   title colour) instead of the app's green. The widget paints its
   configured colours as an inline style on a div inside its shadow
   root, so theme CSS cannot override them; this adopts a stylesheet
   into the widget's open shadow roots instead. --pdp-deep is set on
   <body> and inherits across the shadow boundary. Re-applied when the
   widget swaps its quantity-upsell component (variant changes).
   ============================================================ */
(function () {
  'use strict';
  var CSS =
    '.rc-purchase-option__recommended-badge,.rc-purchase-option__badge{' +
    'background:var(--pdp-deep);color:#fff;border-color:var(--pdp-deep)}' +
    '.rc-purchase-option__best-value-badge{' +
    'background:#fff;color:var(--pdp-deep);border-color:var(--pdp-deep)}';
  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
  var sheet = null;

  function adopt(root) {
    if (!root || (seen && seen.has(root))) return;
    if (seen) seen.add(root);
    try {
      if (root.adoptedStyleSheets && typeof CSSStyleSheet === 'function' &&
          'replaceSync' in CSSStyleSheet.prototype) {
        if (!sheet) { sheet = new CSSStyleSheet(); sheet.replaceSync(CSS); }
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
        return;
      }
    } catch (e) { /* fall through to a style element */ }
    var style = document.createElement('style');
    style.textContent = CSS;
    root.appendChild(style);
  }

  // Style a shadow root and every quantity-upsell component inside it,
  // now and as the widget re-renders.
  function watch(host) {
    var root = host.shadowRoot;
    if (!root) return false;
    if (seen && seen.has(root)) return true;
    adopt(root);
    var scan = function () {
      root.querySelectorAll('recharge-quantity-upsell').forEach(function (el) {
        if (!watch(el)) setTimeout(function () { watch(el); }, 50);
      });
    };
    new MutationObserver(scan).observe(root, { childList: true, subtree: true });
    scan();
    return true;
  }

  function init() {
    var mount = document.querySelector('.product-info__app--buy');
    if (!mount) return;
    var tries = 0;
    var scan = function () {
      var pending = false;
      mount.querySelectorAll('recharge-subscription-widget').forEach(function (el) {
        if (!watch(el)) pending = true;
      });
      // The element upgrades once the app script runs; retry briefly.
      if (pending && tries++ < 100) setTimeout(scan, 100);
    };
    new MutationObserver(scan).observe(mount, { childList: true, subtree: true });
    scan();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
